import { ChangeEvent, KeyboardEvent, useCallback } from 'react';

import { useControllableState } from '@mint-lib/hooks';
import styled from '@mint-lib/styled';
import TextFieldM, {
  BaseTextFieldProps,
  TextFieldProps as TextFieldPropsM,
} from '@mui/material/TextField';
import { TooltipProps as TooltipPropsM } from '@mui/material/Tooltip';

import Label from '../../common/Input/Label.jsx';
import Box from '../Box/Box.jsx';
import FormHelperText from '../FormHelperText/FormHelperText.jsx';
import Icon from '../Icon/Icon.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Truncation from '../Truncation/Truncation.jsx';

export interface CounterProps extends BaseTextFieldProps {
  /**
   * The label content.
   */
  label?: string;
  /**
   * The helper text content.
   */
  helperText?: string;
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder?: string;
  /**
   * The value of the `Input` element, required for a controlled component.
   */
  value?: number;
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it: https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill.
   */
  autoComplete?: string;
  /**
   * Override or extend the styles applied to the component. See CSS API: https://mui.com/api/text-field/#css.
   */
  classes?: TextFieldPropsM['classes'];
  /** The size of the component */
  size?: 'small' | 'medium';
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * @default primary
   */
  color?: 'primary' | 'secondary';
  /**
   * If 'dense' or 'normal', will adjust vertical spacing of this and contained components.
   */
  margin?: TextFieldPropsM['margin'];
  /** The default value. Use when the component is not controlled. */
  defaultValue?: number;
  /** If 'true', the component is disabled. */
  disabled?: boolean;
  /**
   * If 'true', the label is displayed as required and the input will be required.
   */
  required?: boolean;
  /** If 'true', the label is displayed in an error state. */
  error?: boolean;
  /**
   * If 'true', the input will take up the full width of its container.
   */
  fullWidth?: boolean;
  /**
   * If 'true', the input element is focused during the first mount.
   */
  autoFocus?: boolean;
  /**
   * The id of the input element. Use this prop to make label and helperText accessible for screen readers.
   */
  id?: string;
  /**
   * Callback fired when the value is changed.
   */
  onChange: (value: number) => void;
  /** The system prop that allows defining
   * system overrides as well as additional CSS styles.
   * See the `sx` page for more details. https://mui.com/system/the-sx-prop/ */
  sx?: TextFieldPropsM['sx'];
  InputLabelProps?: TextFieldPropsM['InputLabelProps'];
  InputProps?: TextFieldPropsM['InputProps'];
  readOnly?: boolean;
  warning?: boolean;
  infoText?: string;
  InfoTextTooltipProps?: Partial<TooltipPropsM>;
  HelperTextTooltipProps?: Partial<TooltipPropsM>;
  helperTextWidth?: string;
  min: number;
  max: number;
  className?: string;
  width?: string;
  isIntegerOnly?: boolean;
}

/**
 * @deprecated Use Counter from @mint-lib/tw-components/patterns instead
 */
function Counter({
  InputLabelProps,
  readOnly,
  label,
  helperText,
  error,
  warning,
  infoText,
  InfoTextTooltipProps,
  HelperTextTooltipProps,
  fullWidth,
  helperTextWidth = '12.5rem',
  onChange,
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  className,
  width,
  size = 'medium',
  disabled = false,
  isIntegerOnly = false,
  ...props
}: CounterProps) {
  const [value, setValue] = useControllableState({
    prop: controlledValue,
    defaultProp: defaultValue,
    onChange,
  });

  const getWidth = () => {
    if (fullWidth) {
      return '100%';
    }
    if (readOnly && size === 'small') {
      return '13.5rem';
    }
    if (readOnly && size === 'medium') {
      return '13.75rem';
    }
    return '200px';
  };

  const handleIncrease = useCallback(() => {
    if (value < max && !disabled) {
      setValue(value + 1);
    }
  }, [value, disabled, max, setValue]);

  const handleDecrease = useCallback(() => {
    if (value > min && !disabled) {
      setValue(value - 1);
    }
  }, [value, disabled, min, setValue]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = Number(event.target.value);

      if (inputValue >= min && inputValue <= max) {
        setValue(inputValue);
      }
    },
    [min, max, setValue],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isIntegerOnly && event.key === '.') {
        event.preventDefault();
      }
    },
    [],
  );

  return (
    <Box>
      {label ? (
        <Label
          disabled={disabled}
          label={label}
          infoText={infoText}
          TooltipProps={InfoTextTooltipProps}
        />
      ) : null}
      <InputContainer>
        <StyledTextField
          {...props}
          type="number"
          sx={{
            width: getWidth(),
          }}
          variant="outlined"
          error={error}
          size={size}
          readOnly={readOnly}
          label=""
          helperText=""
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            min: min,
            max: max,
            ...props.inputProps,
          }}
          InputProps={{
            className: `${readOnly ? 'read-only-input' : ''} ${
              warning ? 'warning-input' : ''
            }`,
            readOnly: readOnly,
            disabled: disabled,
            ...props.InputProps,
          }}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {!readOnly ? (
          <CaretContainer $size={size} $disabled={disabled}>
            <IncreaseButton
              disabled={disabled}
              onClick={handleIncrease}
              icon="CaretUp"
              color="secondary"
              variant="icon"
              $size={size}
            />
            <DecreaseButton
              disabled={disabled}
              onClick={handleDecrease}
              icon="CaretDown"
              color="secondary"
              $size={size}
              variant="icon"
            />
          </CaretContainer>
        ) : null}
      </InputContainer>

      {helperText ? (
        <StyledFormHelperText
          sx={{
            width: getWidth(),
          }}
          $warning={warning}
          error={error}
          disabled={disabled}
        >
          {warning && !error ? <Icon component="WarningAltFilled" /> : null}
          {error ? <Icon component="WarningFilled" /> : null}
          <Truncation
            tooltipTitle={helperText ? helperText : ''}
            tooltipPlacement="bottom"
            variant="helperText01"
            TooltipProps={HelperTextTooltipProps}
          >
            {helperText}
          </Truncation>
        </StyledFormHelperText>
      ) : null}
    </Box>
  );
}

export default Counter;

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== '$warning',
})<{
  $warning?: boolean;
}>`
  display: flex;
  gap: 5px;
  color: ${({ $warning, theme }) =>
    $warning ? theme.palette.warning.main : ''};
`;
const InputContainer = styled('div')`
  display: flex;
  & .MuiInput-root:not(.Mui-focused) {
    border-bottom: 2px solid transparent;
  }
  & .MuiIconButton-root {
    padding: 0;
    border-radius: 0;
    &:focus::after {
      content: none;
    }
  }
`;

const IncreaseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{
  $size?: 'small' | 'medium';
}>`
  border-top-right-radius: 4px !important;
  color: ${({ theme, disabled }) =>
    disabled
      ? theme.palette.action.disabled01
      : theme.palette.icon.secondary} !important;
  background-color: ${({ theme, disabled }) =>
    disabled
      ? theme.palette.action.disabledBackground
      : theme.palette.ui.field02} !important;
  box-shadow: ${({ theme, disabled }) =>
    disabled
      ? `inset 0 1px 0 0 ${theme.palette.action.disabled01}, inset -1px 0 0 ${theme.palette.action.disabled01} !important`
      : `inset 0 1px 0 0 ${theme.palette.border.primary}, inset -1px 0 0 ${theme.palette.border.primary}`};
  :focus,
  :hover {
    box-shadow: ${({ theme }) =>
      `inset 0 1px 0 0 ${theme.palette.border.primary}, inset -1px 0 0 ${theme.palette.border.primary}`};
  }
  ${({ $size }) =>
    $size === 'small'
      ? 'height: 16px; width: 16px;'
      : 'height: 20px; width: 20px;'}
`;

const DecreaseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{
  $size?: 'small' | 'medium';
}>`
  border-bottom-right-radius: 4px !important;
  color: ${({ theme, disabled }) =>
    disabled
      ? theme.palette.action.disabled01
      : theme.palette.icon.secondary} !important;
  background-color: ${({ theme, disabled }) =>
    disabled
      ? theme.palette.action.disabledBackground
      : theme.palette.ui.field02} !important;
  box-shadow: ${({ theme, disabled }) =>
    disabled
      ? `inset 0 1px 0 0 ${theme.palette.border.disabled}, inset -1px 0 0 ${theme.palette.border.disabled}, inset 0 -1px 0 0 ${theme.palette.border.disabled}`
      : `inset 0 1px 0 0 ${theme.palette.border.primary}, inset -1px 0 0 ${theme.palette.border.primary}, inset 0 -1px 0 0 ${theme.palette.border.primary}`};
  :focus,
  :hover {
    box-shadow: ${({ theme }) =>
      `inset 0 1px 0 0 ${theme.palette.border.primary}, inset -1px 0 0 ${theme.palette.border.primary}, inset 0 -1px 0 0 ${theme.palette.border.primary}`};
  }
  ${({ $size }) =>
    $size === 'small'
      ? 'height: 16px; width: 16px;'
      : 'height: 20px; width: 20px;'}
`;

const CaretContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$size' && prop !== '$disabled',
})<{
  $size?: 'small' | 'medium';
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: ${({ $size }) => ($size === 'small' ? '32px' : '40px')};
  cursor: ${({ $disabled }) => ($disabled ? 'auto' : 'pointer')};
  justify-content: center;
`;

const StyledTextField = styled(TextFieldM)<{
  readOnly?: boolean;
}>`
  & .MuiOutlinedInput-root {
    border-top-right-radius: ${({ readOnly }) =>
      readOnly ? '4px' : '0px'} !important;
    border-bottom-right-radius: ${({ readOnly }) =>
      readOnly ? '4px' : '0px'} !important;
  }
`;
